import { useState, useCallback, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { TV_DATA_QUERY_KEY } from '@/hooks/useTvData'
import { APP_SETTINGS_SIGNED_TITLE_KEY, APP_SETTINGS_QUERY_KEY } from '@/config/appSettingsKeys'
import { openNativeFilePicker } from '@/lib/openNativeFilePicker'

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

type AccessibilityType = 'ad' | 'subtitles' | 'bsl'

interface BroadcasterAccessibilityUploadProps {
  userId: string
  /** When true (e.g. admin page), list shows all uploads and upload_type (Webapp/n8n) and status. */
  showAllUploads?: boolean
  /** Hide web CSV upload form (Drive-only ingest on Listings management). */
  hideUploadForm?: boolean
  /** After bulk delete, call rebuild_listings_accessibility_flags (admin). */
  rebuildAfterDelete?: boolean
}

export default function BroadcasterAccessibilityUpload({
  userId,
  showAllUploads,
  hideUploadForm,
  rebuildAfterDelete,
}: BroadcasterAccessibilityUploadProps) {
  const queryClient = useQueryClient()
  const csvInputRef = useRef<HTMLInputElement>(null)
  const [accessibilityType, setAccessibilityType] = useState<AccessibilityType | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data: signedTitleSetting } = useQuery({
    queryKey: APP_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', APP_SETTINGS_SIGNED_TITLE_KEY)
        .maybeSingle()
      if (err) return false
      return data?.value === 'true'
    },
  })
  const includeSignedTitle = signedTitleSetting ?? false

  const signedTitleMutation = useMutation({
    mutationFn: async (checked: boolean) => {
      const value = checked ? 'true' : 'false'
      const { error: upsertError } = await supabase.from('app_settings').upsert(
        { key: APP_SETTINGS_SIGNED_TITLE_KEY, value, updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      )
      if (upsertError) throw upsertError
    },
    onSuccess: (_data, checked) => {
      queryClient.setQueryData(APP_SETTINGS_QUERY_KEY, checked)
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
    },
  })

  const handleSignedTitleChange = useCallback(
    (checked: boolean) => {
      signedTitleMutation.mutate(checked)
    },
    [signedTitleMutation]
  )

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!accessibilityType || !file) throw new Error('Select a type and choose a file.')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated.')
      if (!supabaseUrl) throw new Error('Missing Supabase URL.')

      const uploadId = crypto.randomUUID()
      const filePath = `accessibility/${userId}/${uploadId}_${file.name}`

      // 1. Insert accessibility_uploads row (use uploadId as id so path is traceable)
      const { error: insertError } = await supabase.from('accessibility_uploads').insert({
        id: uploadId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        uploaded_by: userId,
        accessibility_type: accessibilityType,
        format: 'csv',
        status: 'received',
        upload_source: 'webapp',
      })

      if (insertError) throw new Error(`Failed to create upload record: ${insertError.message}`)

      // 2. Upload file to Storage
      const { error: uploadError } = await supabase.storage.from('tv-schedules').upload(filePath, file, {
        upsert: false,
        contentType: 'text/csv',
      })

      if (uploadError) {
        await supabase.from('accessibility_uploads').update({ status: 'failed', error_summary: uploadError.message }).eq('id', uploadId)
        throw new Error(`Failed to upload file: ${uploadError.message}`)
      }

      // 3. Call Edge Function to parse CSV and insert overlays
      const url = `${supabaseUrl}/functions/v1/process_accessibility_upload`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ upload_id: uploadId }),
      })

      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean
        rowsInserted?: number
        skipped?: number
        error?: string
      }

      if (!res.ok) {
        throw new Error(data?.error ?? `Request failed (${res.status})`)
      }

      if (!data.success) {
        throw new Error(data?.error ?? 'Processing failed')
      }

      return {
        rowsInserted: data.rowsInserted ?? 0,
        skipped: data.skipped ?? 0,
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['accessibility-uploads', userId] })
      setFile(null)
      setSuccessMessage(
        `Accessibility file uploaded and processed. ${result.rowsInserted} rows added.${result.skipped > 0 ? ` (${result.skipped} rows skipped)` : ''}`
      )
      setError(null)
    },
    onError: (err: Error) => {
      setError(err.message)
      setSuccessMessage(null)
    },
  })

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    setFile(f ?? null)
    setError(null)
    setSuccessMessage(null)
  }, [])

  const openAccessibilityCsvPicker = useCallback(() => {
    openNativeFilePicker(csvInputRef.current)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)
    if (!accessibilityType) {
      setError('Please select an accessibility type (AD, Subtitles, or BSL).')
      return
    }
    if (!file) {
      setError('Please choose a CSV file.')
      return
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('File must be a CSV (.csv).')
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size must be 10MB or less.`)
      return
    }
    uploadMutation.mutate()
  }

  return (
    <section
      className={hideUploadForm ? '' : 'rounded-lg p-4 border'}
      style={
        hideUploadForm
          ? undefined
          : { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }
      }
      aria-labelledby={hideUploadForm ? undefined : 'accessibility-upload-heading'}
    >
      {!hideUploadForm && (
        <>
      <h2 id="accessibility-upload-heading" className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
        Upload accessibility file (CSV)
      </h2>
      <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
        Upload a CSV that lists which programmes have Audio Description (AD), Subtitles, or BSL. Select the type below before uploading. File must be CSV, max 10MB, UTF-8 (no BOM).
      </p>
      <div className="text-sm mb-4 p-3 rounded border space-y-2" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Required columns</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>channel</strong> or <strong>channel_id</strong> — Channel identifier (e.g. bbc1.uk, cbbc.uk). Must match the main TV listings feed.</li>
          <li><strong>start</strong> — Full date and time of the programme start. Use a format JavaScript can parse, e.g. ISO 8601: <code className="text-xs bg-black/10 dark:bg-white/10 px-1 rounded">2026-03-21T13:00:00+00:00</code> or <code className="text-xs bg-black/10 dark:bg-white/10 px-1 rounded">2026-03-21T13:00:00Z</code>. Time-only values (e.g. 13:00) are not accepted; the date must be included.</li>
        </ul>
        <p className="font-medium mt-2" style={{ color: 'var(--text-primary)' }}>Optional columns</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>stop</strong>, <strong>end</strong>, or <strong>finish</strong> — Full date and time of the programme end (same format as start).</li>
          <li><strong>title</strong> — Programme title (used when matching to listings).</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <span className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            What does this file contain?
          </span>
          <div className="flex flex-wrap gap-2">
            {(['ad', 'subtitles', 'bsl'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setAccessibilityType(type)
                  setError(null)
                }}
                className="px-4 py-2 rounded font-medium transition-colors"
                style={{
                  backgroundColor: accessibilityType === type ? 'var(--bg-interactive)' : 'var(--bg-tertiary)',
                  color: accessibilityType === type ? 'var(--text-inverse)' : 'var(--text-primary)',
                }}
              >
                {type === 'ad' ? 'AD' : type === 'subtitles' ? 'Subtitles' : 'BSL'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label
            htmlFor="accessibility-csv-open"
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--text-primary)' }}
          >
            CSV file
          </label>
          <div className="max-w-md">
            <div className="sr-only">
              <input
                ref={csvInputRef}
                id="accessibility-csv"
                type="file"
                accept=".csv,text/csv"
                tabIndex={-1}
                onChange={handleFileChange}
              />
            </div>
            <button
              id="accessibility-csv-open"
              type="button"
              onClick={openAccessibilityCsvPicker}
              className="min-h-[44px] rounded border-2 px-3 py-2 text-sm font-semibold"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              Choose file…
            </button>
          </div>
          {file && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm" style={{ color: 'var(--color-error)' }} role="alert">
            {error}
          </p>
        )}
        {successMessage && (
          <p className="text-sm" style={{ color: 'var(--color-success, green)' }} role="status">
            {successMessage}
          </p>
        )}

        <button
          type="submit"
          disabled={!accessibilityType || !file || uploadMutation.isPending}
          className="px-4 py-2 rounded font-semibold disabled:opacity-50"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          {uploadMutation.isPending ? 'Uploading…' : 'Upload and process'}
        </button>
      </form>
        </>
      )}

      <div className="mb-4 p-3 rounded border" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeSignedTitle}
            onChange={(e) => handleSignedTitleChange(e.target.checked)}
            disabled={signedTitleMutation.isPending}
            className="rounded"
            aria-describedby="signed-title-desc"
          />
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            Search listings for signed content (BSL)
            {signedTitleMutation.isPending ? ' (saving…)' : ''}
          </span>
        </label>
        <p id="signed-title-desc" className="text-sm mt-1 ml-6" style={{ color: 'var(--text-secondary)' }}>
          When enabled, programmes whose title starts with &quot;Signed&quot; (e.g. &quot;Signed: News&quot;) are marked as BSL in the TV guide for <strong>all users</strong>.
        </p>
      </div>

      <RecentAccessibilityUploads
        userId={userId}
        showAll={showAllUploads}
        rebuildAfterDelete={rebuildAfterDelete}
      />
    </section>
  )
}

interface AccessibilityUploadRow {
  id: string
  file_path: string | null
  file_name: string
  accessibility_type: string | null
  status: string
  upload_source: string | null
  created_at: string
  overlay_count?: number
}

function TrashIcon({ className, size = 18 }: { className?: string; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

function RecentAccessibilityUploads({
  userId,
  showAll,
  rebuildAfterDelete,
}: {
  userId: string
  showAll?: boolean
  rebuildAfterDelete?: boolean
}) {
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [rebuildMessage, setRebuildMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: uploads } = useQuery({
    queryKey: ['accessibility-uploads', userId, showAll ?? false],
    queryFn: async () => {
      let query = supabase
        .from('accessibility_uploads_with_overlay_count')
        .select('id, file_path, file_name, accessibility_type, status, upload_source, created_at, overlay_count')
        .order('created_at', { ascending: false })
        .limit(showAll ? 100 : 10)
      if (!showAll) {
        query = query.eq('uploaded_by', userId)
      }
      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as AccessibilityUploadRow[]
    },
    enabled: !!userId || !!showAll,
  })

  const bulkRemoveMutation = useMutation({
    mutationFn: async (items: { id: string; file_path: string | null }[]) => {
      const paths = items.map((i) => i.file_path).filter((p): p is string => !!p?.trim())
      if (paths.length > 0) {
        await supabase.storage.from('tv-schedules').remove(paths)
      }
      const ids = items.map((i) => i.id)
      const { error } = await supabase.from('accessibility_uploads').delete().in('id', ids)
      if (error) throw new Error(error.message)
    },
    onSuccess: async (_data, items) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        items.forEach((i) => next.delete(i.id))
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['accessibility-uploads', userId] })
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })

      if (rebuildAfterDelete) {
        const { error: rebuildError } = await supabase.rpc('rebuild_listings_accessibility_flags')
        if (rebuildError) {
          setRebuildMessage({ type: 'error', text: `Deleted uploads but rebuild failed: ${rebuildError.message}` })
        } else {
          setRebuildMessage({
            type: 'success',
            text: `Removed ${items.length} upload(s) and rebuilt TV accessibility flags.`,
          })
        }
        setTimeout(() => setRebuildMessage(null), 8000)
      }
    },
  })

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (!uploads?.length) return
    const allSelected = uploads.every((u) => selectedIds.has(u.id))
    setSelectedIds(allSelected ? new Set() : new Set(uploads.map((u) => u.id)))
  }, [uploads, selectedIds])

  const handleBulkRemove = useCallback(() => {
    const toRemove = uploads?.filter((u) => selectedIds.has(u.id)) ?? []
    if (toRemove.length === 0) return
    if (!window.confirm(`Remove ${toRemove.length} upload(s) and all their overlay data? This cannot be undone.`)) return
    bulkRemoveMutation.mutate(toRemove.map((u) => ({ id: u.id, file_path: u.file_path ?? null })))
  }, [uploads, selectedIds, bulkRemoveMutation])

  const uploadTypeLabel = (source: string | null | undefined) =>
    source === 'n8n' ? 'n8n' : 'Webapp'

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const selectedCount = selectedIds.size
  const allSelected = uploads && uploads.length > 0 && uploads.every((u) => selectedIds.has(u.id))

  if (!uploads?.length) {
    if (showAll) {
      return (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          No accessibility uploads yet.
        </p>
      )
    }
    return null
  }

  return (
    <div className={showAll && rebuildAfterDelete ? '' : 'mt-6 pt-4 border-t'} style={{ borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
          {showAll ? 'All accessibility uploads' : 'Recent accessibility uploads'}
        </h3>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={handleBulkRemove}
            disabled={bulkRemoveMutation.isPending}
            className="flex items-center gap-2 text-sm px-3 py-1.5 rounded border font-medium"
            style={{
              borderColor: 'var(--color-error, #b91c1c)',
              color: 'var(--color-error, #b91c1c)',
              backgroundColor: 'transparent',
            }}
            aria-label={`Delete ${selectedCount} selected upload(s)`}
          >
            <TrashIcon size={16} />
            {bulkRemoveMutation.isPending ? 'Deleting…' : `Delete selected (${selectedCount})`}
          </button>
        )}
      </div>
      {rebuildMessage && (
        <p
          className="mb-2 text-sm"
          role="alert"
          style={{ color: rebuildMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}
        >
          {rebuildMessage.text}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" style={{ color: 'var(--text-secondary)' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              <th className="text-left py-2 pr-2 w-10" style={{ color: 'var(--text-primary)' }}>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                    className="rounded"
                  />
                </label>
              </th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>File name</th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>Type</th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>Upload type</th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>Status</th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>Overlays</th>
              <th className="text-left py-2 pr-4 font-medium" style={{ color: 'var(--text-primary)' }}>Date & time</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td className="py-2 pr-2">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleSelected(u.id)}
                      aria-label={`Select ${u.file_name}`}
                      className="rounded"
                    />
                  </label>
                </td>
                <td className="py-2 pr-4" style={{ color: 'var(--text-primary)' }}>{u.file_name}</td>
                <td className="py-2 pr-4">{u.accessibility_type ?? '—'}</td>
                <td className="py-2 pr-4">
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                    }}
                    title="Upload type"
                  >
                    {uploadTypeLabel(u.upload_source)}
                  </span>
                </td>
                <td className="py-2 pr-4">
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: u.status === 'parsed' ? 'var(--color-success, #16a34a)' : u.status === 'failed' ? 'var(--color-error, #b91c1c)' : 'var(--bg-tertiary)',
                      color: u.status === 'parsed' || u.status === 'failed' ? 'white' : 'var(--text-primary)',
                    }}
                    title="Status"
                  >
                    {u.status}
                  </span>
                </td>
                <td className="py-2 pr-4" title="Overlay records">
                  {typeof u.overlay_count === 'number' ? u.overlay_count : '—'}
                </td>
                <td className="py-2 pr-4">{formatDateTime(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
